import React from "react";
import { constraintInfo, constraintAxis } from "../../../utils/p6";
import { formatDateMonthDay } from "../../../utils/format";
import "./ConstraintBadge.css";

interface Props {
  cstrType: string | null | undefined;
  cstrDate: string | null | undefined;
  /** The activity's computed dates, used to flag the constraint as driving when
   *  the relevant one lands on the constraint date. Pass whatever the surface
   *  has (drive dates / cascade new dates / display dates). */
  startDate?: string | null;
  finishDate?: string | null;
  /** Render only when the constraint acts on this axis — lets a surface drop the
   *  badge into a Start column and a Finish column and have each self-select. */
  axis?: "start" | "finish";
  /** Compact form ("Start ≥ 6/15") for tight spots like the gantt / date cells;
   *  default is the full label ("Start on or after 6/15"). */
  compact?: boolean;
  /** This constraint is currently VIOLATED (the date was pushed past it) — flag
   *  the badge red with a trailing asterisk. */
  violated?: boolean;
}

/** Small chip surfacing a P6 date constraint (e.g. "Start on or after 6/15").
 *  Emphasized when the constraint is what's holding the date rather than a
 *  predecessor. Renders nothing when the activity has no constraint. */
const ConstraintBadge: React.FC<Props> = ({
  cstrType,
  cstrDate,
  startDate,
  finishDate,
  axis,
  compact = false,
  violated = false,
}) => {
  if (axis && constraintAxis(cstrType) !== axis) return null;
  const info = constraintInfo(cstrType, cstrDate, startDate, finishDate);
  if (!info) return null;
  return (
    <span
      className={`constraint-badge${info.driving ? " constraint-badge-driving" : ""}${violated ? " constraint-badge-violated" : ""}`}
      title={
        `${info.label} ${info.date}` +
        (violated ? " — VIOLATED (date pushed past this constraint)" :
          info.driving ? " — driving this date (not a dependency)" : "")
      }
    >
      <span className="constraint-badge-icon" aria-hidden>
        ⚲
      </span>
      {compact ? info.short : info.label} {formatDateMonthDay(info.date)}
      {violated && <span className="constraint-badge-star" aria-hidden>*</span>}
    </span>
  );
};

export default ConstraintBadge;
